/**
 * Field-level "newest edit wins" merge for user data.
 *
 * Every user edit is recorded as a change with the time it happened (epoch
 * milliseconds, which are timezone independent). A record stores the time of the
 * last accepted edit per field in `fieldUpdatedAt`. When changes are applied, a
 * change wins only if it is at least as new as the stored time for that field.
 *
 * Most fields are compared as a whole. `lastReadPages` and `stats` are compared
 * per entry (per content type and per day) so two devices can edit different
 * entries without overwriting each other.
 *
 * This file has no browser or server dependencies: the client and the
 * `/api/v2/storage` endpoint use the same functions.
 */

type UserDataRecord = Record<string, any>

/** Changed values plus the time each change happened, keyed by change key. */
export type UserDataChanges = {
  /**
   * Changed top-level values. Nested fields hold only changed entries;
   * a `null` entry means the entry was removed.
   */
  values: UserDataRecord
  /** Change key (`notes`, `lastReadPages/kuran`, `stats/19-10-2026`) to epoch ms. */
  changedAt: Record<string, number>
}

/** Fields merged per entry instead of as a whole value. */
export const NESTED_USER_DATA_FIELDS = ['lastReadPages', 'stats']

/** Fields owned by the server or by identity, never accepted as user changes. */
export const PROTECTED_USER_DATA_FIELDS = [
  'uid',
  'email',
  'displayName',
  'createdAt',
  'updatedAt',
  'fieldUpdatedAt',
  'nativeState',
  'nativeStateMetadata',
  // Never a real field: assigning it would replace an object's prototype.
  '__proto__',
]

const CHANGE_KEY_SEPARATOR = '/'

/** Check whether a value is a JSON object (not an array or null). */
export const isPlainObject = (value: unknown): value is UserDataRecord =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

/** Return a record's version: its `updatedAt`, else `createdAt`, else `null`. */
export const getUserDataVersion = (
  data: { updatedAt?: string | null; createdAt?: string | null } | null | undefined,
) => data?.updatedAt || data?.createdAt || null

/** Compare two JSON values by content. */
const isSameValue = (first: unknown, second: unknown) =>
  JSON.stringify(first) === JSON.stringify(second)

/** Check whether a field is merged per entry. */
const isNestedField = (field: string) => NESTED_USER_DATA_FIELDS.includes(field)

/** Check whether a field may be changed by a user edit. */
const isEditableField = (field: string) => !PROTECTED_USER_DATA_FIELDS.includes(field)

/** Build the change key of a whole field or of one entry in a nested field. */
const toChangeKey = (field: string, entryKey?: string) =>
  entryKey === undefined ? field : `${field}${CHANGE_KEY_SEPARATOR}${entryKey}`

/** Split a change key into its field and, for nested fields, its entry key. */
const fromChangeKey = (changeKey: string) => {
  const separatorIndex = changeKey.indexOf(CHANGE_KEY_SEPARATOR)
  if (separatorIndex === -1) return { field: changeKey, entryKey: undefined }
  return {
    field: changeKey.slice(0, separatorIndex),
    entryKey: changeKey.slice(separatorIndex + 1),
  }
}

/** Create an empty change set. */
export const createEmptyUserDataChanges = (): UserDataChanges => ({
  values: {},
  changedAt: {},
})

/** Check whether a change set contains at least one change. */
export const hasUserDataChanges = (changes: UserDataChanges | null | undefined) =>
  Boolean(changes && Object.keys(changes.changedAt).length > 0)

/** Record changed entries of a nested field such as `stats` or `lastReadPages`. */
const addNestedFieldChanges = (
  changes: UserDataChanges,
  field: string,
  previousValue: unknown,
  nextValue: UserDataRecord,
  time: number,
) => {
  const previousEntries = isPlainObject(previousValue) ? previousValue : {}
  const entryKeys = new Set([...Object.keys(previousEntries), ...Object.keys(nextValue)])

  entryKeys.forEach((entryKey) => {
    if (isSameValue(previousEntries[entryKey], nextValue[entryKey])) return

    changes.values[field] = changes.values[field] || {}
    changes.values[field][entryKey] = nextValue[entryKey] ?? null
    changes.changedAt[toChangeKey(field, entryKey)] = time
  })
}

/**
 * Create a change set from a local edit. Only values that really differ from the
 * previous record are included, so unchanged fields never overwrite newer edits
 * made on another device.
 */
export const createUserDataChanges = (
  previousData: UserDataRecord,
  updates: UserDataRecord,
  time: number,
): UserDataChanges => {
  const changes = createEmptyUserDataChanges()

  Object.entries(updates).forEach(([field, value]) => {
    if (!isEditableField(field)) return

    if (isNestedField(field)) {
      // The stats reset sends an empty array; it means "no entries left".
      const nextEntries = Array.isArray(value) && value.length === 0 ? {} : value
      // A missing nested value is not treated as "remove every entry".
      if (isPlainObject(nextEntries)) {
        addNestedFieldChanges(changes, field, previousData[field], nextEntries, time)
      }
      return
    }

    if (isSameValue(previousData[field], value)) return
    // JSON drops `undefined`, so a cleared field would never reach the server.
    changes.values[field] = value === undefined ? null : value
    changes.changedAt[field] = time
  })

  return changes
}

/** Copy one change (value and time) from a change set into another. */
const copyChange = (
  target: UserDataChanges,
  source: UserDataChanges,
  changeKey: string,
) => {
  const { field, entryKey } = fromChangeKey(changeKey)

  if (entryKey === undefined) {
    target.values[field] = source.values[field]
  } else {
    target.values[field] = { ...(target.values[field] || {}) }
    target.values[field][entryKey] = source.values[field]?.[entryKey] ?? null
  }
  target.changedAt[changeKey] = source.changedAt[changeKey]
}

/** Remove one change (value and time) from a change set. */
const removeChange = (changes: UserDataChanges, changeKey: string) => {
  const { field, entryKey } = fromChangeKey(changeKey)

  if (entryKey === undefined) {
    delete changes.values[field]
  } else if (changes.values[field]) {
    delete changes.values[field][entryKey]
    if (Object.keys(changes.values[field]).length === 0) delete changes.values[field]
  }
  delete changes.changedAt[changeKey]
}

/** Deep-copy a change set, so the copy can be edited without touching the original. */
const cloneUserDataChanges = (changes: UserDataChanges): UserDataChanges =>
  JSON.parse(JSON.stringify(changes))

/** Combine two change sets; changes in `newerChanges` replace the same keys. */
export const combineUserDataChanges = (
  existingChanges: UserDataChanges,
  newerChanges: UserDataChanges,
): UserDataChanges => {
  const combined = cloneUserDataChanges(existingChanges)
  Object.keys(newerChanges.changedAt).forEach((changeKey) =>
    copyChange(combined, newerChanges, changeKey),
  )
  return combined
}

/** Read the value of one change; nested entries without a value read as `null`. */
const readChangeValue = (changes: UserDataChanges, changeKey: string) => {
  const { field, entryKey } = fromChangeKey(changeKey)
  if (entryKey === undefined) return changes.values[field]
  return changes.values[field]?.[entryKey] ?? null
}

/**
 * Remove changes that were uploaded and not edited again since. A change edited
 * during the upload has a newer time or another value, and stays pending.
 */
export const removeUploadedUserDataChanges = (
  pendingChanges: UserDataChanges,
  uploadedChanges: UserDataChanges,
): UserDataChanges => {
  const remaining = cloneUserDataChanges(pendingChanges)
  Object.entries(uploadedChanges.changedAt).forEach(([changeKey, time]) => {
    const isSameTime = remaining.changedAt[changeKey] === time
    const isSameEdit =
      isSameTime &&
      isSameValue(
        readChangeValue(remaining, changeKey),
        readChangeValue(uploadedChanges, changeKey),
      )
    if (isSameEdit) removeChange(remaining, changeKey)
  })
  return remaining
}

/** Write one change value into a record, removing nested entries set to `null`. */
const writeChangeValue = (
  record: UserDataRecord,
  changes: UserDataChanges,
  changeKey: string,
) => {
  const { field, entryKey } = fromChangeKey(changeKey)

  if (entryKey === undefined) {
    record[field] = changes.values[field]
    return
  }

  const entries = { ...(isPlainObject(record[field]) ? record[field] : {}) }
  const value = changes.values[field]?.[entryKey]
  if (value === null || value === undefined) {
    delete entries[entryKey]
  } else {
    entries[entryKey] = value
  }
  record[field] = entries
}

/**
 * Apply changes to a record: each change wins only when it is at least as new as
 * the record's last accepted edit of that field (or entry). Fields without a
 * stored time count as the oldest possible edit.
 */
export const applyUserDataChanges = <T extends UserDataRecord>(
  record: T,
  changes: UserDataChanges,
): T => {
  const result: UserDataRecord = {
    ...record,
    fieldUpdatedAt: { ...(record.fieldUpdatedAt || {}) },
  }

  Object.entries(changes.changedAt).forEach(([changeKey, time]) => {
    const storedTime = result.fieldUpdatedAt[changeKey] ?? 0
    if (time < storedTime) return

    writeChangeValue(result, changes, changeKey)
    result.fieldUpdatedAt[changeKey] = time
  })

  return result as T
}

/** Check whether Firestore accepts a map key: not empty and not reserved (`__name__`). */
const isStorableKey = (key: string) => key !== '' && !/^__.*__$/.test(key)

/**
 * Check whether Firestore can store a value. JSON values are storable except
 * arrays directly inside arrays and maps with empty or reserved keys.
 */
const isStorableValue = (value: unknown, isInsideArray = false): boolean => {
  if (Array.isArray(value)) {
    return !isInsideArray && value.every((item) => isStorableValue(item, true))
  }
  if (isPlainObject(value)) {
    return Object.entries(value).every(
      ([key, item]) => isStorableKey(key) && isStorableValue(item),
    )
  }
  return true
}

/** Check whether a change key refers to a field and entry shape that is allowed. */
const isValidChangeKey = (changeKey: string, values: UserDataRecord) => {
  const { field, entryKey } = fromChangeKey(changeKey)
  // Own keys only: `in` would also accept inherited keys such as `constructor`.
  const hasValue = Object.prototype.hasOwnProperty.call(values, field)
  if (!isEditableField(field) || !hasValue || !isStorableKey(field)) return false
  if (isNestedField(field)) {
    return (
      entryKey !== undefined && isStorableKey(entryKey) && isPlainObject(values[field])
    )
  }
  return entryKey === undefined
}

/**
 * Validate a change set received from a client. Unknown shapes, protected fields,
 * and values Firestore cannot store are dropped one change at a time, so a single
 * bad change never makes the whole upload fail (and retry forever). Change times are
 * capped at `now` so a device whose clock runs ahead cannot win future edits.
 */
export const sanitizeIncomingUserDataChanges = (
  rawChanges: unknown,
  now: number,
): UserDataChanges => {
  const safeChanges = createEmptyUserDataChanges()
  if (!isPlainObject(rawChanges)) return safeChanges
  if (!isPlainObject(rawChanges.values) || !isPlainObject(rawChanges.changedAt)) {
    return safeChanges
  }

  const source: UserDataChanges = {
    values: rawChanges.values,
    changedAt: rawChanges.changedAt,
  }
  Object.entries(source.changedAt).forEach(([changeKey, rawTime]) => {
    if (!isValidChangeKey(changeKey, source.values)) return
    if (!isStorableValue(readChangeValue(source, changeKey))) return

    copyChange(safeChanges, source, changeKey)
    const time = Number(rawTime)
    safeChanges.changedAt[changeKey] = Number.isFinite(time) ? Math.min(time, now) : now
  })

  return safeChanges
}

/**
 * Turn a whole-field patch (the old pending-patch format, or a write from an old
 * client) into a change set where every field and nested entry has the given time.
 */
export const createUserDataChangesFromPatch = (
  patch: UserDataRecord,
  time: number,
): UserDataChanges => {
  const changes = createEmptyUserDataChanges()

  Object.entries(patch).forEach(([field, value]) => {
    if (!isEditableField(field)) return

    if (isNestedField(field)) {
      if (!isPlainObject(value)) return
      Object.entries(value).forEach(([entryKey, entryValue]) => {
        changes.values[field] = changes.values[field] || {}
        changes.values[field][entryKey] = entryValue
        changes.changedAt[toChangeKey(field, entryKey)] = time
      })
      return
    }

    changes.values[field] = value
    changes.changedAt[field] = time
  })

  return changes
}
